import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Package, FileText, Award } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Almonds = () => {
  const products = [
    {
      id: 'nonpareil',
      name: 'Nonpareil Almonds',
      bestFor: 'Premium retail, snack packs, gifting, high-end confectionery',
      gradeMeaning: 'Top-tier California variety; thin skin, light color, smooth texture',
      qualityFocus: 'Uniform size, minimal defects, premium appearance',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/bc0fb0d9c602b35a89ce7477b364282e71e964411c16256fe5ed7ec650ffcd76.png'
    },
    {
      id: 'carmel',
      name: 'Carmel Almonds',
      bestFor: 'Roasting, snack mixes, value retail programs',
      gradeMeaning: 'Popular California variety; medium size, good flavor profile',
      qualityFocus: 'Consistent sizing, robust flavor after roasting',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/bc0fb0d9c602b35a89ce7477b364282e71e964411c16256fe5ed7ec650ffcd76.png'
    },
    {
      id: 'nps',
      name: 'NPS (Natural Process Select)',
      bestFor: 'Bulk ingredient supply, bakery, confectionery manufacturing',
      gradeMeaning: 'Mixed variety grade; cost-effective for processing applications',
      qualityFocus: 'Good kernel integrity, suitable for further processing',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/bc0fb0d9c602b35a89ce7477b364282e71e964411c16256fe5ed7ec650ffcd76.png'
    }
  ];

  const processedForms = [
    {
      id: 'whole-natural',
      name: 'Whole Natural Almonds',
      bestFor: 'Retail packs, snacking, roasting',
      description: 'Raw almonds with skin intact'
    },
    {
      id: 'blanched-whole',
      name: 'Blanched Whole Almonds',
      bestFor: 'Confectionery, premium applications, marzipan',
      description: 'Skin removed through blanching process'
    },
    {
      id: 'sliced',
      name: 'Sliced Almonds',
      bestFor: 'Bakery toppings, salads, garnishing',
      description: 'Thinly sliced for visual appeal'
    },
    {
      id: 'slivered',
      name: 'Slivered Almonds',
      bestFor: 'Bakery, confectionery, ice cream toppings',
      description: 'Julienne-cut blanched almonds'
    },
    {
      id: 'diced',
      name: 'Diced Almonds',
      bestFor: 'Bakery inclusions, cereal, snack bars',
      description: 'Uniform small pieces for even distribution'
    },
    {
      id: 'flour',
      name: 'Almond Flour/Meal',
      bestFor: 'Gluten-free baking, macarons, confectionery',
      description: 'Finely ground blanched almonds'
    }
  ];

  const grades = [
    {
      name: 'Standard Export',
      description: 'Best for bulk sourcing and value-led programs',
      features: ['Standard grading + batch COA', 'Aligned to buyer/destination specifications', 'Cost-effective bulk supply']
    },
    {
      name: 'Premium (EU/USA-Ready)',
      description: 'Best for private label and strict QC retail programs',
      features: ['Tighter QC + enhanced traceability', 'Premium packaging options', 'Expanded COA panel as required']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Almonds</h1>
              <p className="text-xl text-slate-700 mb-6">
                Premium California and Mediterranean almonds for retail, HoReCa, and ingredient manufacturing
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-[#263776] text-white px-4 py-2 rounded-full">Multiple Varieties</span>
                <span className="bg-[#F47D20] text-white px-4 py-2 rounded-full">UAE · UK-EU · USA · Africa</span>
                <span className="bg-slate-700 text-white px-4 py-2 rounded-full">Batch COA Always Supplied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Grading Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {grades.map((grade, index) => (
                <Card key={index} className="p-6 border-t-4 border-t-[#263776]">
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-slate-600 mb-4">{grade.description}</p>
                  <ul className="space-y-2">
                    {grade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Almond Varieties */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Almond Varieties</h2>
              <p className="text-lg text-slate-600">Premium California varieties for different applications</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#263776] mb-2">{product.name}</h3>
                    <p className="text-sm text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                    
                    <Accordion type="single" collapsible className="mb-4">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-sm py-2">View Details</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 text-sm">
                            <p><strong>Grade:</strong> {product.gradeMeaning}</p>
                            <p><strong>Quality Focus:</strong> {product.qualityFocus}</p>
                            <p><strong>Packaging:</strong> Bulk cartons | Vacuum packs available</p>
                            <p><strong>Documents:</strong> COA | Spec sheet | Lot-coded packing list</p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <Button asChild size="sm" className="w-full bg-[#263776] hover:bg-[#1e2a5e]">
                      <Link to={`/contact?product=almonds-${product.id}`}>Request Quote</Link>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Processed Forms */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Processed Forms</h2>
              <p className="text-lg text-slate-600">Value-added almond products for specific applications</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processedForms.map((form) => (
                <Card key={form.id} className="p-6 hover:shadow-lg transition-shadow border-l-4 border-l-[#F47D20]">
                  <h3 className="text-lg font-bold text-[#263776] mb-2">{form.name}</h3>
                  <p className="text-sm text-[#F47D20] font-medium mb-2">{form.bestFor}</p>
                  <p className="text-sm text-slate-600 mb-4">{form.description}</p>
                  
                  <Button asChild size="sm" variant="outline" className="w-full border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
                    <Link to={`/contact?product=almonds-${form.id}`}>Request Quote</Link>
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Baseline */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-6 text-center">SPANXPORT Quality Baseline</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#263776] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Moisture Control</h3>
                  <p className="text-sm text-slate-600">Typically 5-6% for optimal shelf life</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Traceability</h3>
                  <p className="text-sm text-slate-600">Lot/production/date codes for full traceability</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Testing</h3>
                  <p className="text-sm text-slate-600">Aflatoxin, salmonella, and pesticide residue testing</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Request a Quote for Almonds</h2>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Share your requirements and we'll respond with offer + lead time + documentation list
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">Back to Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Almonds;
